<template>
  <div class="chatbot">
    <transition name="chat-panel">
      <section
        v-if="abierto"
        class="chat-panel"
        role="dialog"
        aria-label="Asistente CX"
      >
        <header class="chat-head">
          <div>
            <strong>Asistente CX</strong>
            <span class="chat-sub">Consulta datos del CRM</span>
          </div>
          <div class="chat-head-actions">
            <button
              v-if="mensajes.length"
              class="chat-icon-btn"
              title="Nueva conversación"
              @click="reiniciar"
            >
              ↺
            </button>
            <button class="chat-icon-btn" title="Cerrar" @click="abierto = false">
              ✕
            </button>
          </div>
        </header>

        <div ref="lista" class="chat-body">
          <div v-if="!mensajes.length" class="chat-empty">
            <p>Pregúntame sobre incidencias, planes de acción, KPIs o cuentas.</p>
            <button
              v-for="s in sugerencias"
              :key="s"
              class="chat-sugerencia"
              @click="enviar(s)"
            >
              {{ s }}
            </button>
          </div>

          <div
            v-for="(m, i) in mensajes"
            :key="i"
            class="chat-msg"
            :class="m.role"
          >
            <div v-if="m.role === 'assistant'" class="chat-md" v-html="md(m.content)" />
            <template v-else>{{ m.content }}</template>
          </div>

          <div v-if="cargando" class="chat-msg assistant chat-typing">
            <span /><span /><span />
          </div>
          <div v-if="error" class="chat-error">{{ error }}</div>
        </div>

        <form class="chat-input" @submit.prevent="enviar()">
          <textarea
            ref="entrada"
            v-model="texto"
            rows="1"
            maxlength="2000"
            placeholder="Escribe tu pregunta…"
            :disabled="cargando"
            @keydown.enter.exact.prevent="enviar()"
          />
          <button type="submit" :disabled="cargando || !texto.trim()">Enviar</button>
        </form>
      </section>
    </transition>

    <button
      class="chat-fab"
      :aria-label="abierto ? 'Cerrar asistente' : 'Abrir asistente'"
      @click="alternar"
    >
      <svg v-if="!abierto" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <path
          fill="currentColor"
          d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2zm3 6a1.2 1.2 0 1 0 0 2.4A1.2 1.2 0 0 0 7 10zm5 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4zm5 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4z"
        />
      </svg>
      <span v-else aria-hidden="true">✕</span>
    </button>
  </div>
</template>

<script>
import { enviarMensaje } from "@/services/chatbot.service";
import { renderMarkdown } from "@/utils/markdown";

// La conversación sobrevive a recargas y cambios de sección dentro de la
// pestaña, pero no se comparte entre pestañas ni persiste al cerrar sesión.
const LLAVE = "chatbot.mensajes";

export default {
  name: "ChatbotFlotante",
  data() {
    return {
      abierto: false,
      texto: "",
      mensajes: this.cargarGuardados(),
      cargando: false,
      error: "",
      sugerencias: [
        "¿Cómo van los KPIs de CX este año?",
        "¿Qué planes de acción están abiertos?",
        "Incidencias de prioridad Alta en proceso",
      ],
    };
  },
  methods: {
    md(t) {
      return renderMarkdown(t);
    },
    cargarGuardados() {
      try {
        const g = JSON.parse(sessionStorage.getItem(LLAVE) || "[]");
        return Array.isArray(g) ? g : [];
      } catch (e) {
        return [];
      }
    },
    guardar() {
      try {
        sessionStorage.setItem(LLAVE, JSON.stringify(this.mensajes.slice(-20)));
      } catch (e) {
        /* sin almacenamiento: la conversación vive solo en memoria */
      }
    },
    alternar() {
      this.abierto = !this.abierto;
      if (this.abierto) this.$nextTick(() => this.$refs.entrada && this.$refs.entrada.focus());
    },
    reiniciar() {
      this.mensajes = [];
      this.error = "";
      this.guardar();
    },
    bajar() {
      this.$nextTick(() => {
        const l = this.$refs.lista;
        if (l) l.scrollTop = l.scrollHeight;
      });
    },
    async enviar(sugerida) {
      const contenido = (sugerida || this.texto).trim();
      if (!contenido || this.cargando) return;

      this.error = "";
      this.texto = "";
      this.mensajes.push({ role: "user", content: contenido });
      this.cargando = true;
      this.bajar();

      try {
        const r = await enviarMensaje(this.mensajes.slice(-20));
        this.mensajes.push({ role: "assistant", content: r.respuesta || "(sin respuesta)" });
      } catch (e) {
        this.error = e.message;
        // Se retira la pregunta fallida para que el historial siga alternando
        // user/assistant, y se devuelve al campo para reintentar.
        this.mensajes.pop();
        this.texto = contenido;
      } finally {
        this.cargando = false;
        this.guardar();
        this.bajar();
      }
    },
  },
};
</script>

<style scoped>
.chatbot {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1060; /* por encima del header (50) y de los modales de Bootstrap (1055) */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.chat-fab {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  color: var(--on-accent, #fff);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.chat-fab:hover {
  transform: scale(1.06);
}

.chat-panel {
  width: min(380px, calc(100vw - 32px));
  height: min(560px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  background: var(--white);
  color: var(--txt);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.chat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  color: var(--on-accent, #fff);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
}
.chat-head strong {
  display: block;
  font-size: 15px;
}
.chat-sub {
  font-size: 12px;
  opacity: 0.85;
}
.chat-head-actions {
  display: flex;
  gap: 4px;
}
.chat-icon-btn {
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: inherit;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
}
.chat-icon-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--surface);
}

.chat-empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: var(--txt-dim);
}
.chat-sugerencia {
  text-align: left;
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
}
.chat-sugerencia:hover {
  background: var(--surface-2);
}

.chat-msg {
  max-width: 88%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.chat-msg.user {
  align-self: flex-end;
  color: var(--on-accent, #fff);
  background: var(--primary);
  border-bottom-right-radius: 4px;
}
.chat-msg.assistant {
  align-self: flex-start;
  background: var(--white);
  border: 1px solid var(--border);
  border-bottom-left-radius: 4px;
  white-space: normal;
}

/* Contenido Markdown del asistente (v-html: requiere :deep) */
.chat-md :deep(p),
.chat-md :deep(ul),
.chat-md :deep(ol) {
  margin: 0 0 6px;
}
.chat-md :deep(ul),
.chat-md :deep(ol) {
  padding-left: 18px;
}
.chat-md :deep(h1),
.chat-md :deep(h2),
.chat-md :deep(h3),
.chat-md :deep(h4) {
  font-size: 14px;
  font-weight: 700;
  margin: 8px 0 4px;
}
.chat-md :deep(hr) {
  margin: 8px 0;
}
.chat-md :deep(code) {
  font-size: 12px;
  background: var(--surface-2);
  padding: 1px 4px;
  border-radius: 4px;
}

.chat-typing {
  display: flex;
  gap: 4px;
}
.chat-typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--txt-faint);
  animation: chat-pulso 1s infinite ease-in-out;
}
.chat-typing span:nth-child(2) {
  animation-delay: 0.15s;
}
.chat-typing span:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes chat-pulso {
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}

.chat-error {
  font-size: 13px;
  color: #dc2626;
  background: rgba(220, 38, 38, 0.08);
  border-radius: 8px;
  padding: 8px 10px;
}

.chat-input {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--border);
  background: var(--white);
}
.chat-input textarea {
  flex: 1;
  resize: none;
  max-height: 100px;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 14px;
  color: var(--txt);
  background: var(--surface);
}
.chat-input textarea:focus {
  outline: 2px solid var(--primary);
  outline-offset: -1px;
}
.chat-input button {
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  font-weight: 600;
  color: var(--on-accent, #fff);
  background: var(--primary);
  cursor: pointer;
}
.chat-input button:disabled {
  opacity: 0.5;
  cursor: default;
}

.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.chat-panel-enter-from,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 480px) {
  .chatbot {
    right: 16px;
    bottom: 16px;
  }
}
</style>
